import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Package,
  GitBranch,
  GraduationCap,
  Play,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  GitCommit,
  ArrowRight,
  ExternalLink,
  Settings,
  Key,
  Scale,
  Container,
  Cloud,
  Globe,
  Bug,
  Lock,
  Eye,
  FileSearch,
  Network,
  Target,
  Shield,
  ClipboardCheck,
  Crosshair,
  Skull,
  Atom,
  Wrench,
  Wifi,
  Siren,
  BarChart3,
  Zap,
  PlayCircle,
  CheckSquare,
  Square,
} from 'lucide-react';
import type { ProjectType } from '@nirvik/shared';
import { apiClient } from '@/lib/api-client';
import { useAgentActivity } from '@/hooks/use-agent-activity';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DomainGateBanner } from '@/components/domain/domain-gate-banner';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentConfig {
  repoUrl?: string;
  branch?: string;
  agents?: Record<
    string,
    { enabled: boolean; schedule?: string; lastRun?: string }
  >;
}

interface AgentRun {
  runId: string;
  agentType: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  createdAt: string;
  duration: number | null;
  findingCount: number;
}

interface AgentDef {
  type: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  available: boolean;
  category: string;
  /**
   * Which project-types this agent can run against. Omitted = legacy
   * blue-team agent; treated as ['repo', 'hybrid'].
   */
  supportedProjectTypes?: ProjectType[];
  /** Team classification (for filtering / badges). Defaults to 'blue'. */
  team?: 'blue' | 'red' | 'cross-cutting';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AGENT_CATEGORIES = [
  'Code Analysis',
  'Dependencies & Supply Chain',
  'Infrastructure & Cloud',
  'API & Access Control',
  'Data & Compliance',
  'Threat Intelligence',
  'Operations & Monitoring',
] as const;

const AGENTS: AgentDef[] = [
  // ── Code Analysis ──
  {
    type: 'code-vuln',
    name: 'Code Vulnerability Scanner',
    description: 'AI-powered source code analysis that finds security vulnerabilities and explains how to fix them',
    icon: ShieldCheck,
    color: 'blue',
    available: true,
    category: 'Code Analysis',
  },
  {
    type: 'sast',
    name: 'SAST Engine',
    description: 'Pattern-first static analysis with LLM verification — finds injection, XSS, and dangerous patterns',
    icon: Bug,
    color: 'rose',
    available: true,
    category: 'Code Analysis',
  },
  {
    type: 'secret-scanner',
    name: 'Secret Scanner',
    description: 'Detects hardcoded API keys, tokens, passwords, and private keys in your codebase',
    icon: Key,
    color: 'red',
    available: true,
    category: 'Code Analysis',
  },
  {
    type: 'crypto-audit',
    name: 'Cryptography Audit',
    description: 'Reviews cryptographic implementations for weak algorithms, key management, and JWT security',
    icon: Lock,
    color: 'violet',
    available: true,
    category: 'Code Analysis',
  },
  {
    type: 'quantum-readiness',
    name: 'Quantum Readiness',
    description: 'Tier-classifies the project against post-quantum cryptography. Inspects TLS hybrid KEM, cert chains, JWT signing, library versions; maps to NIST PQC + NSA CNSA 2.0',
    icon: Atom,
    color: 'violet',
    available: true,
    category: 'Code Analysis',
    team: 'cross-cutting',
    supportedProjectTypes: ['repo', 'endpoint', 'hybrid'],
  },
  {
    type: 'malware-detect',
    name: 'Malicious Code Detection',
    description: 'Scans for obfuscated code, backdoors, crypto miners, and suspicious patterns',
    icon: Skull,
    color: 'red',
    available: true,
    category: 'Code Analysis',
  },

  // ── Dependencies & Supply Chain ──
  {
    type: 'dependency',
    name: 'Dependency Scanner',
    description: 'Checks all packages against the OSV.dev vulnerability database with upgrade recommendations',
    icon: Package,
    color: 'emerald',
    available: true,
    category: 'Dependencies & Supply Chain',
  },
  {
    type: 'license-compliance',
    name: 'License Compliance',
    description: 'Identifies copyleft, restrictive, and unknown licenses in your dependency tree',
    icon: Scale,
    color: 'amber',
    available: true,
    category: 'Dependencies & Supply Chain',
  },
  {
    type: 'supply-chain',
    name: 'Supply Chain Security',
    description: 'Detects typosquatting, malicious install scripts, unpinned deps, and CI pipeline injection risks',
    icon: FileSearch,
    color: 'orange',
    available: true,
    category: 'Dependencies & Supply Chain',
  },

  // ── Infrastructure & Cloud ──
  {
    type: 'container-security',
    name: 'Container Security',
    description: 'Analyzes Dockerfiles and docker-compose configs for security misconfigurations',
    icon: Container,
    color: 'cyan',
    available: true,
    category: 'Infrastructure & Cloud',
  },
  {
    type: 'iac-scanner',
    name: 'Infrastructure as Code',
    description: 'Scans Terraform, CloudFormation, and CDK templates for insecure configurations',
    icon: Cloud,
    color: 'sky',
    available: true,
    category: 'Infrastructure & Cloud',
  },
  {
    type: 'cloud-config',
    name: 'Cloud Configuration',
    description: 'Reviews AWS, GCP, and Azure SDK usage for overly permissive IAM and exposed resources',
    icon: Globe,
    color: 'indigo',
    available: true,
    category: 'Infrastructure & Cloud',
  },
  {
    type: 'config-audit',
    name: 'Configuration Audit',
    description: 'Checks app configs, nginx, .env files for debug modes, weak sessions, and insecure defaults',
    icon: Wrench,
    color: 'slate',
    available: true,
    category: 'Infrastructure & Cloud',
  },
  {
    type: 'network-security',
    name: 'Network Security',
    description: 'Finds missing TLS, disabled certificate verification, HTTP-only endpoints, and cleartext traffic',
    icon: Wifi,
    color: 'teal',
    available: true,
    category: 'Infrastructure & Cloud',
  },
  {
    type: 'cicd-gate',
    name: 'CI/CD Security Gate',
    description: 'Analyzes GitHub Actions, GitLab CI, and Jenkins pipelines for injection and secret exposure risks',
    icon: GitBranch,
    color: 'purple',
    available: true,
    category: 'Infrastructure & Cloud',
  },

  // ── API & Access Control ──
  {
    type: 'api-security',
    name: 'API Security',
    description: 'Identifies unauthenticated endpoints, missing rate limiting, IDOR, and excessive data exposure',
    icon: Network,
    color: 'fuchsia',
    available: true,
    category: 'API & Access Control',
  },
  {
    type: 'access-control',
    name: 'Access Control Analysis',
    description: 'Detects privilege escalation paths, missing authorization, IDOR, and broken access control',
    icon: Shield,
    color: 'emerald',
    available: true,
    category: 'API & Access Control',
  },

  // ── Data & Compliance ──
  {
    type: 'data-privacy',
    name: 'Data Privacy',
    description: 'Finds PII/PHI in logs, error messages, and unencrypted storage — GDPR/CCPA gap analysis',
    icon: Eye,
    color: 'pink',
    available: true,
    category: 'Data & Compliance',
  },
  {
    type: 'compliance',
    name: 'Compliance Mapping',
    description: 'Maps findings against SOC 2, PCI DSS, HIPAA, and OWASP ASVS controls using code evidence',
    icon: ClipboardCheck,
    color: 'lime',
    available: true,
    category: 'Data & Compliance',
    team: 'cross-cutting',
    // Skill maps existing findings + code evidence to controls; the
    // harness errors out without a repo (REPO_REQUIRED_AGENTS).
    supportedProjectTypes: ['repo', 'hybrid'],
  },

  // ── Threat Intelligence ──
  {
    type: 'threat-model',
    name: 'AI Threat Model',
    description: 'Generates a STRIDE threat model from codebase architecture — trust boundaries, dataflows, assets, threats + mitigations',
    icon: Target,
    color: 'amber',
    available: true,
    category: 'Threat Intelligence',
    team: 'cross-cutting',
    // Skill is "based on codebase analysis" (per SKILL.md) and the
    // harness fails fast without a repo (REPO_REQUIRED_AGENTS).
    supportedProjectTypes: ['repo', 'hybrid'],
  },
  {
    type: 'pentest-recon',
    name: 'Attack Surface Recon',
    description: 'Maps your attack surface like a penetration tester — endpoints, admin routes, file uploads',
    icon: Crosshair,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
  },
  {
    type: 'endpoint-surface',
    name: 'Endpoint Surface Assessment',
    description: 'Probes live endpoints for TLS, header, and open-port exposure — then reasons across the combined output to prioritize real risks',
    icon: Wifi,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },
  {
    type: 'webapp-pentest',
    name: 'Web App Pentest',
    description: 'Dynamic webapp probing: forms, sensitive-path enumeration, reflection canary, then agentic synthesis of follow-up tests',
    icon: Crosshair,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },
  {
    type: 'api-pentest',
    name: 'API Pentest',
    description: 'OpenAPI / GraphQL-driven API probing — auth drift, BOLA/IDOR candidates, chain-attack hypotheses',
    icon: Bug,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },
  {
    type: 'auth-attack',
    name: 'Auth Attack',
    description: 'Enumerates login/OAuth endpoints, audits cookie flags, decodes JWTs, flags weak auth configurations',
    icon: Lock,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },
  {
    type: 'asset-recon',
    name: 'Asset Recon',
    description: 'Subdomain enumeration, SPF/DMARC audit, dangling-CNAME takeover detection',
    icon: FileSearch,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },
  {
    type: 'breach-intel',
    name: 'Breach Intel',
    description: 'Cross-references certificate-transparency logs and GitHub code search for exposed credentials',
    icon: Eye,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },
  {
    type: 'graphql-pentest',
    name: 'GraphQL Pentest',
    description: 'GraphQL-specific probing: introspection, depth-bomb, alias amplification, batching + LLM follow-ups',
    icon: Network,
    color: 'red',
    available: true,
    category: 'Threat Intelligence',
    team: 'red',
    supportedProjectTypes: ['endpoint', 'hybrid'],
  },

  // ── Operations & Monitoring ──
  {
    type: 'incident-response',
    name: 'Incident Response Readiness',
    description: 'Assesses logging, monitoring, alerting, and audit trail completeness for IR preparedness',
    icon: Siren,
    color: 'orange',
    available: true,
    category: 'Operations & Monitoring',
    team: 'cross-cutting',
    // Pure repo analysis: globs runbooks, alarms-as-code, structured-logging
    // patterns. Endpoint-only projects have nothing for it to inspect.
    supportedProjectTypes: ['repo', 'hybrid'],
  },
  {
    type: 'security-score',
    name: 'Security Scorecard',
    description: 'Comprehensive 0-100 scoring across auth, crypto, config, logging, and more — summarizes findings from any agent',
    icon: BarChart3,
    color: 'blue',
    available: true,
    category: 'Operations & Monitoring',
    team: 'cross-cutting',
    supportedProjectTypes: ['repo', 'endpoint', 'hybrid'],
  },
  {
    type: 'education',
    name: 'Security Education',
    description: 'Generates plain-language explanations and learning content for every finding',
    icon: GraduationCap,
    color: 'amber',
    available: true,
    category: 'Operations & Monitoring',
    team: 'cross-cutting',
    supportedProjectTypes: ['repo', 'endpoint', 'hybrid'],
  },
];

const COLOR_MAP: Record<
  string,
  { bg: string; text: string; accent: string }
> = {
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', accent: 'bg-blue-600 hover:bg-blue-500' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', accent: 'bg-emerald-600 hover:bg-emerald-500' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-700 dark:text-purple-400', accent: 'bg-purple-600 hover:bg-purple-500' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', accent: 'bg-amber-600 hover:bg-amber-500' },
  red: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', accent: 'bg-red-600 hover:bg-red-500' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-700 dark:text-rose-400', accent: 'bg-rose-600 hover:bg-rose-500' },
  violet: { bg: 'bg-violet-500/10', text: 'text-violet-700 dark:text-violet-400', accent: 'bg-violet-600 hover:bg-violet-500' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-700 dark:text-cyan-400', accent: 'bg-cyan-600 hover:bg-cyan-500' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-700 dark:text-sky-400', accent: 'bg-sky-600 hover:bg-sky-500' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-700 dark:text-indigo-400', accent: 'bg-indigo-600 hover:bg-indigo-500' },
  pink: { bg: 'bg-pink-500/10', text: 'text-pink-700 dark:text-pink-400', accent: 'bg-pink-600 hover:bg-pink-500' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400', accent: 'bg-orange-600 hover:bg-orange-500' },
  lime: { bg: 'bg-lime-500/10', text: 'text-lime-400', accent: 'bg-lime-600 hover:bg-lime-500' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-700 dark:text-teal-400', accent: 'bg-teal-600 hover:bg-teal-500' },
  fuchsia: { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-700 dark:text-fuchsia-400', accent: 'bg-fuchsia-600 hover:bg-fuchsia-500' },
  slate: { bg: 'bg-slate-500/10', text: 'text-slate-700 dark:text-slate-400', accent: 'bg-slate-600 hover:bg-slate-500' },
};

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; icon: React.ElementType; animate?: boolean }
> = {
  queued: { bg: 'bg-gray-500/10', text: 'text-muted-foreground', icon: Clock },
  running: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', icon: Loader2, animate: true },
  completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  failed: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', icon: AlertTriangle },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentsPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();
  const enabled = !!orgId && !!portfolioId && !!projectId;
  const basePath = `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`;
  const queryClient = useQueryClient();
  const { trackRun, trackRuns } = useAgentActivity();

  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  // ---- Queries ----

  const { data: configRes, isLoading: configLoading } = useQuery({
    queryKey: ['agent-configs', orgId, portfolioId, projectId],
    queryFn: () => apiClient.get(`${basePath}/agents/config`),
    enabled,
  });

  // Project record is the source of truth for repo/endpoint target configuration.
  // Legacy agent-config endpoint is a fallback for pre-Phase-A records.
  const { data: projectRes } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId!, portfolioId!, projectId!),
    enabled,
  });
  const project = projectRes?.data as
    | {
        projectType?: ProjectType;
        domainVerified?: boolean;
        verifiableHost?: string | null;
        repoConfig?: { gitUrl: string; branch: string; provider?: string };
        endpointConfig?: {
          baseUrl: string;
          hostnames?: string[];
          auth?: { kind: string };
          spec?: { kind: string; url?: string; uploadedKey?: string };
        };
      }
    | undefined;
  const projectType: ProjectType = project?.projectType ?? 'repo';
  // Endpoint/hybrid projects are blocked from running anything until their
  // domain is verified. The backend enforces this too (API + dispatchers);
  // here we disable the controls and explain why.
  const domainBlocked = projectType !== 'repo' && project?.domainVerified === false;

  const { data: runsRes, isLoading: runsLoading } = useQuery({
    queryKey: ['agent-runs', orgId, portfolioId, projectId],
    queryFn: () => apiClient.get(`${basePath}/agents/runs`),
    enabled,
  });

  const configData = configRes?.data as Record<string, unknown> | undefined;
  const config: AgentConfig | undefined = configData
    ? { repoUrl: configData.repoUrl as string, branch: configData.branch as string, agents: configData.agents as AgentConfig['agents'] }
    : undefined;
  const runsData = runsRes?.data as { runs?: AgentRun[] } | undefined;
  const runs: AgentRun[] = runsData?.runs ?? [];
  const isLoading = configLoading || runsLoading;

  // Resolved target — prefer structured project config, fall back to legacy.
  const repoUrl: string | undefined = project?.repoConfig?.gitUrl ?? config?.repoUrl;
  const repoBranch: string | undefined = project?.repoConfig?.branch ?? config?.branch;
  const endpointBaseUrl: string | undefined = project?.endpointConfig?.baseUrl;

  // ---- Mutations ----

  const startRun = useMutation({
    mutationFn: (params: { agentType: string; config: Record<string, unknown> }) =>
      apiClient.post(`${basePath}/agents/run`, params),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-runs', orgId, portfolioId, projectId] });
      const data = res.data as { runId?: string } | undefined;
      if (data?.runId && projectId) {
        trackRun(orgId!, portfolioId!, projectId!, data.runId, variables.agentType);
      }
    },
  });

  const startMultiRun = useMutation({
    mutationFn: (params: { agents?: string[]; mode?: string; config: Record<string, unknown> }) =>
      apiClient.post(`${basePath}/agents/run-multi`, params),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['agent-runs', orgId, portfolioId, projectId] });
      setSelectedAgents(new Set());
      const data = res.data as { runs?: Array<{ runId: string; agentType: string }> } | undefined;
      if (data?.runs && projectId) {
        trackRuns(orgId!, portfolioId!, projectId!, data.runs);
      }
    },
  });

  // ---- Derived state ----

  // "Runnable" means the project has the config an agent needs to do useful work:
  // repo project → a git URL; endpoint project → a base URL; hybrid → either/both.
  // Agents themselves still enforce type-specific requirements via supportedProjectTypes.
  const hasRepoTarget = !!repoUrl;
  const hasEndpointTarget = !!endpointBaseUrl;
  const hasTarget =
    (projectType === 'repo' && hasRepoTarget) ||
    (projectType === 'endpoint' && hasEndpointTarget) ||
    (projectType === 'hybrid' && (hasRepoTarget || hasEndpointTarget));

  function getLatestRun(agentType: string): AgentRun | undefined {
    return runs
      .filter((r) => r.agentType === agentType)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }

  function agentSupportsProjectType(agent: AgentDef, pt: ProjectType): boolean {
    const supported = agent.supportedProjectTypes ?? ['repo', 'hybrid'];
    return supported.includes(pt);
  }

  const visibleAgents = AGENTS.filter((a) => agentSupportsProjectType(a, projectType));

  function getAgentStatus(agent: AgentDef): 'ready' | 'running' | 'not-configured' | 'coming-soon' {
    if (!agent.available) return 'coming-soon';
    const latest = getLatestRun(agent.type);
    if (latest?.status === 'running' || latest?.status === 'queued') return 'running';
    if (!hasTarget) return 'not-configured';
    return 'ready';
  }

  function toggleAgent(type: string) {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  // Passed through to the agent-run endpoint for legacy repo agents that
  // still read config.repoUrl / config.branch. Endpoint agents load the
  // endpointConfig directly from DynamoDB (Phase A+) — nothing to pass here.
  const runConfig = { repoUrl, branch: repoBranch };

  // ---- Loading skeleton ----

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-muted rounded w-48 mb-2" />
        <div className="h-4 bg-gray-100 dark:bg-muted rounded w-72" />
        <Card className="p-6 animate-pulse">
          <div className="h-10 bg-gray-100 dark:bg-muted rounded w-full mb-3" />
          <div className="h-10 bg-gray-100 dark:bg-muted rounded w-2/3" />
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 bg-gray-100 dark:bg-muted rounded-lg" />
                <div className="h-4 bg-gray-200 dark:bg-muted rounded w-32" />
              </div>
              <div className="h-3 bg-gray-100 dark:bg-muted rounded w-full mb-2" />
              <div className="h-3 bg-gray-100 dark:bg-muted rounded w-3/4" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ---- Render ----

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">
          Security Agents
        </h1>
        <p className="text-muted-foreground dark:text-muted-foreground mt-1">
          {visibleAgents.filter((a) => a.available).length} AI-powered security agents compatible with this {projectType} project
        </p>
      </div>

      {/* Domain not verified → block all runs */}
      {domainBlocked && (
        <DomainGateBanner
          orgId={orgId!} portfolioId={portfolioId!} projectId={projectId!}
          domain={project?.verifiableHost ?? endpointBaseUrl}
        />
      )}

      {/* Smart Scan & Multi-Agent Actions */}
      {hasTarget && !domainBlocked && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={startMultiRun.isPending}
            onClick={() => startMultiRun.mutate({ mode: 'smart', config: runConfig })}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white"
          >
            {startMultiRun.isPending && startMultiRun.variables?.mode === 'smart' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Smart Scan
          </Button>
          <Button
            variant="outline"
            disabled={startMultiRun.isPending}
            onClick={() => startMultiRun.mutate({ mode: 'all', config: runConfig })}
            className="bg-muted hover:bg-muted text-foreground border-border"
          >
            {startMultiRun.isPending && startMultiRun.variables?.mode === 'all' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4 mr-2" />
            )}
            Run All Agents
          </Button>
          {selectedAgents.size > 0 && (
            <Button
              variant="outline"
              disabled={startMultiRun.isPending}
              onClick={() =>
                startMultiRun.mutate({ agents: Array.from(selectedAgents), config: runConfig })
              }
              className="bg-blue-900/30 hover:bg-blue-800/40 text-blue-300 border-blue-700"
            >
              {startMultiRun.isPending && startMultiRun.variables?.agents ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Selected ({selectedAgents.size})
            </Button>
          )}
          {startMultiRun.isError && (
            <p className="text-xs text-red-700 dark:text-red-400">{(startMultiRun.error as Error).message}</p>
          )}
        </div>
      )}

      {/* Target info — read-only. Editing lives in Project Settings. */}
      <Card className="bg-card border border-border rounded-2xl p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-wrap">
            {hasRepoTarget && (
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <GitCommit className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2 truncate">
                    {repoUrl}
                    {repoUrl && (
                      <a href={repoUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground shrink-0">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">Branch: {repoBranch ?? 'main'}</p>
                </div>
              </div>
            )}
            {hasEndpointTarget && (
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
                  <Globe className="h-5 w-5 text-indigo-700 dark:text-indigo-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2 truncate">
                    {endpointBaseUrl}
                    {endpointBaseUrl && (
                      <a href={endpointBaseUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground shrink-0">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Endpoint
                    {project?.endpointConfig?.auth?.kind && project.endpointConfig.auth.kind !== 'none'
                      ? ` · auth: ${project.endpointConfig.auth.kind}`
                      : ''}
                    {project?.endpointConfig?.spec?.kind && project.endpointConfig.spec.kind !== 'none'
                      ? ` · spec: ${project.endpointConfig.spec.kind}`
                      : ''}
                  </p>
                </div>
              </div>
            )}
            {!hasRepoTarget && !hasEndpointTarget && (
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">No target configured</p>
                  <p className="text-xs text-muted-foreground">
                    Configure a repo or endpoint in Project Settings before running agents.
                  </p>
                </div>
              </div>
            )}
          </div>
          <Link
            to={`${basePath}/settings`}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0"
          >
            <Settings className="h-3.5 w-3.5" /> Configure in Settings
          </Link>
        </div>
      </Card>

      {/* Agents grouped by category (filtered by project type) */}
      {AGENT_CATEGORIES.map((category) => {
        const categoryAgents = visibleAgents.filter((a) => a.category === category);
        if (categoryAgents.length === 0) return null;

        return (
          <div key={category}>
            <h2 className="text-sm font-bold text-muted-foreground dark:text-muted-foreground uppercase tracking-widest mb-3">
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryAgents.map((agent) => {
                const colors = COLOR_MAP[agent.color] || COLOR_MAP.blue;
                const status = getAgentStatus(agent);
                const latestRun = getLatestRun(agent.type);
                const Icon = agent.icon;
                const isRunning =
                  startRun.isPending &&
                  (startRun.variables as { agentType: string } | undefined)?.agentType === agent.type;

                return (
                  <Card
                    key={agent.type}
                    className="bg-card border border-border rounded-2xl p-5 hover:border-border transition-colors relative overflow-hidden"
                  >
                    {!agent.available && (
                      <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
                        <span className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border border-yellow-500/20 text-sm font-medium px-4 py-2 rounded-full">
                          Coming Soon
                        </span>
                      </div>
                    )}

                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center shrink-0', colors.bg)}>
                          <Icon className={cn('h-4.5 w-4.5', colors.text)} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-foreground truncate">{agent.name}</h3>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 text-[10px] font-medium mt-0.5 px-1.5 py-0.5 rounded-full',
                              status === 'ready' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
                              status === 'running' && 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
                              status === 'not-configured' && 'bg-gray-500/10 text-muted-foreground',
                              status === 'coming-soon' && 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
                            )}
                          >
                            {status === 'running' && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />}
                            {status === 'ready' && 'Ready'}
                            {status === 'running' && 'Running'}
                            {status === 'not-configured' && 'Not Configured'}
                            {status === 'coming-soon' && 'Coming Soon'}
                          </span>
                        </div>
                      </div>
                      {agent.available && hasTarget && (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleAgent(agent.type); }}
                          className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                          title={selectedAgents.has(agent.type) ? 'Deselect agent' : 'Select agent'}
                        >
                          {selectedAgents.has(agent.type) ? (
                            <CheckSquare className="h-4.5 w-4.5 text-blue-700 dark:text-blue-400" />
                          ) : (
                            <Square className="h-4.5 w-4.5" />
                          )}
                        </button>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{agent.description}</p>

                    {latestRun && (
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-3">
                        <Clock className="h-3 w-3" />
                        {formatRelativeTime(latestRun.createdAt)}
                        {latestRun.findingCount > 0 && (
                          <>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-orange-700 dark:text-orange-400">{latestRun.findingCount} findings</span>
                          </>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        disabled={!agent.available || !hasTarget || domainBlocked || status === 'running' || isRunning}
                        onClick={() =>
                          startRun.mutate({
                            agentType: agent.type,
                            config: runConfig,
                          })
                        }
                        className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1.5 h-auto"
                      >
                        {isRunning || status === 'running' ? (
                          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                        ) : (
                          <Play className="h-3 w-3 mr-1.5" />
                        )}
                        Run
                      </Button>
                      {agent.available && (
                        <Link
                          to={`${basePath}/agents/${agent.type}`}
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                        >
                          Details <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Recent runs */}
      {runs.length > 0 && (
        <div>
          <h2 className="text-base font-bold text-foreground dark:text-foreground uppercase tracking-wide mb-4">
            Recent Agent Runs
          </h2>
          <div className="flex flex-col gap-3">
            {runs.slice(0, 8).map((run) => {
              const st = STATUS_STYLES[run.status] ?? STATUS_STYLES.queued;
              const StatusIcon = st.icon;
              const agentDef = AGENTS.find((a) => a.type === run.agentType);
              return (
                <Link key={run.runId} to={`${basePath}/agents/runs/${run.runId}`} className="block">
                  <Card className="bg-card border border-border rounded-xl p-4 hover:border-border transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center', st.bg)}>
                          <StatusIcon className={cn('h-4 w-4', st.text, st.animate && 'animate-spin')} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{agentDef?.name ?? run.agentType}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                            <span>{formatRelativeTime(run.createdAt)}</span>
                            {run.findingCount > 0 && (
                              <>
                                <span className="text-muted-foreground">|</span>
                                <span>{run.findingCount} findings</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full capitalize', st.bg, st.text)}>
                        {run.status}
                      </span>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
